/**
 * The shared vocabulary of the Wend platform.
 *
 * These enums are the SINGLE SOURCE OF TRUTH. They are declared here once and
 * mirrored as Postgres enum types by `@wend/db`, which imports the `*_VALUES`
 * tuples below rather than re-typing the members — that is what stops the TS
 * enums and the pg enums drifting apart.
 *
 * The full set from CONTRACT.md is declared here (they are cheap and they are
 * shared vocabulary). A Postgres enum type is only created by `@wend/db` in the
 * slice that first needs it; slice 0 creates `user_role` and `feature` only.
 */
import { z } from 'zod';

/** Delivery channels a message can go out on. */
export const CHANNEL_VALUES = ['email', 'whatsapp'] as const;
export const channelSchema = z.enum(CHANNEL_VALUES);
export type Channel = z.infer<typeof channelSchema>;

/** Template kinds. Only `email` and `whatsapp` are creatable in v1. */
export const TEMPLATE_TYPE_VALUES = ['email', 'whatsapp', 'sms', 'instagram'] as const;
export const templateTypeSchema = z.enum(TEMPLATE_TYPE_VALUES);
export type TemplateType = z.infer<typeof templateTypeSchema>;

export const TEMPLATE_STATUS_VALUES = ['draft', 'pending', 'approved', 'rejected'] as const;
export const templateStatusSchema = z.enum(TEMPLATE_STATUS_VALUES);
export type TemplateStatus = z.infer<typeof templateStatusSchema>;

/**
 * Where a campaign is in its life.
 *
 * The first seven were fixed by CONTRACT.md and created whole by
 * `0010_campaigns.sql`. Slice 18 appends two, and **the order matters**: a
 * Postgres enum remembers the order its labels were created in, `ALTER TYPE …
 * ADD VALUE` appends, and `packages/db/src/schema/campaigns.ts` hands this exact
 * tuple to `pgEnum`. Inserting a member in the middle here would make the mirror
 * describe an enum this database does not have.
 *
 * ## The two slice 18 adds, and why they are two rather than one
 *
 *   `holding_between_waves`  part of a large list has been sent and the rest is
 *                            waiting for the hold to elapse and the delivery
 *                            reports to come back. **It releases itself.**
 *   `halted_delivery_risk`   sending stopped because of how the messages already
 *                            sent were received. **A person must resume it.**
 *
 * `paused_insufficient_credits` is the precedent for a non-terminal pause, and
 * these are the same kind of state: everyone not yet reached stays `pending`,
 * nobody is skipped, and nothing has to be rebuilt.
 *
 * They are two members and never one, because a user must never be shown a
 * holding campaign described as a halted one. Waiting twenty minutes and having
 * been stopped for a bad list are different facts with different next actions,
 * and a single `paused` member would have made the *sentence* the only thing
 * distinguishing them — which is the class of defect slice 17 spent a whole
 * slice removing.
 *
 * `halted_delivery_risk` is deliberately **channel-neutral**. The specific
 * reason lives in `campaigns.halt_reason`, whose vocabulary is
 * `CAMPAIGN_HALT_REASON_VALUES` in `./contracts/list-safety.js`, because
 * `CAMPAIGN_STATUS_DISPLAY` is keyed on status alone — the known wart slice 17
 * recorded, where `sending` says "handed to Amazon SES" on a WhatsApp campaign.
 * Baking `hard_bounce` into the status name would have reproduced that fault
 * knowingly on a state WhatsApp can also reach.
 *
 * `flow` (slice 68, 0050) is the status of a flow's STEP campaign: one row per
 * (flow version, node, cycle) whose recipients are added one person at a time
 * as they reach the node, so every flow message goes through the campaign send
 * path. It is pinned in the database to the four `flow_*` columns, both ways,
 * and it is not a Connect state: the scheduler, the dispatcher and every
 * Connect list and count exclude it. It arrives last so the enum's order —
 * which pgEnum mirrors — is append-only.
 */
export const CAMPAIGN_STATUS_VALUES = [
  'draft',
  'scheduled',
  'sending',
  'paused_insufficient_credits',
  'sent',
  'failed',
  'cancelled',
  'holding_between_waves',
  'halted_delivery_risk',
  // Slice 68, migration 0050: a flow's message step. Never a Connect state —
  // see the paragraph below.
  'flow',
] as const;
export const campaignStatusSchema = z.enum(CAMPAIGN_STATUS_VALUES);
export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

/**
 * Every status a CONNECT campaign can hold — all but `flow`. What a Connect
 * list, filter or count offers; a flow's step campaigns are read from the flow.
 */
export const CONNECT_CAMPAIGN_STATUS_VALUES = CAMPAIGN_STATUS_VALUES.filter(
  (status): status is Exclude<CampaignStatus, 'flow'> => status !== 'flow',
);
export type ConnectCampaignStatus = Exclude<CampaignStatus, 'flow'>;

export const RECIPIENT_STATUS_VALUES = [
  'pending',
  'sent',
  'delivered',
  'bounced',
  'failed',
  'skipped',
] as const;
export const recipientStatusSchema = z.enum(RECIPIENT_STATUS_VALUES);
export type RecipientStatus = z.infer<typeof recipientStatusSchema>;

export const MESSAGE_STATUS_VALUES = [
  'queued',
  'sent',
  'delivered',
  'bounced',
  'failed',
  'unknown',
] as const;
export const messageStatusSchema = z.enum(MESSAGE_STATUS_VALUES);
export type MessageStatus = z.infer<typeof messageStatusSchema>;

export const LEDGER_ENTRY_TYPE_VALUES = [
  'grant',
  'reserve',
  'release',
  'consume',
  'refund',
  'adjustment',
] as const;
export const ledgerEntryTypeSchema = z.enum(LEDGER_ENTRY_TYPE_VALUES);
export type LedgerEntryType = z.infer<typeof ledgerEntryTypeSchema>;

/**
 * `ses` | `meta_whatsapp` | `meta_ads`.
 *
 * The first two were created together by migration 0006, on the argument that
 * an enum is vocabulary fixed by CONTRACT.md and creating half of it would make
 * a later slice an `ALTER TYPE` for no gain. `meta_ads` was not foreseen there,
 * so slice 40 pays exactly that cost — and it is paid in **two** migrations,
 * because Postgres will add an enum label inside a transaction but refuses to
 * let the same transaction use it. 0027 adds the label, 0028 uses it.
 *
 * `meta_ads` is its own kind rather than a field on the `meta_whatsapp` row.
 * The two reach the same Graph host with the same kind of token and expire
 * independently; sharing a row would expire them together on a screen while
 * Meta expires them apart, and the consequences differ — a stale WhatsApp token
 * stops a message, a stale ads token stops Wend pausing something that is
 * spending money.
 */
export const INTEGRATION_KIND_VALUES = ['ses', 'meta_whatsapp', 'meta_ads'] as const;
export const integrationKindSchema = z.enum(INTEGRATION_KIND_VALUES);
export type IntegrationKind = z.infer<typeof integrationKindSchema>;

export const INTEGRATION_STATUS_VALUES = [
  'disconnected',
  'connected',
  'failed',
  'expiring',
] as const;
export const integrationStatusSchema = z.enum(INTEGRATION_STATUS_VALUES);
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

/** Role a user holds inside their tenant. Mirrored as pg enum `user_role`. */
export const USER_ROLE_VALUES = ['owner', 'member'] as const;
export const userRoleSchema = z.enum(USER_ROLE_VALUES);
export type UserRole = z.infer<typeof userRoleSchema>;

/**
 * Product pillars a tenant can be entitled to. Mirrored as pg enum `feature`,
 * **in the database's order** — `connect` was added `AFTER 'reach'` by
 * `migrations/0047_feature_connect.sql`, so the enum's `enumsortorder` is
 * reach, connect, flow, sense and this tuple says the same.
 *
 * Slice 65 split the old Reach in two. Before it, "reach" meant every way a
 * workspace talked to people: email and WhatsApp campaigns *and* Meta ads. The
 * two halves answer to different rules — on an owned channel Wend holds the
 * person and writes a receipt per message; on a paid one the platform holds the
 * people, the figures are aggregate and the money is a budget — so they are two
 * pillars now:
 *
 *   - `reach`   — paid acquisition. Meta ads on the workspace's own ad account.
 *   - `connect` — owned channels. Email and WhatsApp campaigns.
 *   - `flow`    — not built.
 *   - `sense`   — the measurement pillar (slices 58-64).
 *
 * Every workspace that had `reach` before the split kept it and gained
 * `connect` (`0048_connect_entitlement.sql`), so nobody lost a screen.
 *
 * Display order is **not** this order — see {@link PILLAR_ORDER}. The rejected
 * alternative was to reorder this tuple to the display order: harmless at
 * runtime, since Drizzle does not check an enum's label order, but it would make
 * "mirrors the pg enum" a sentence that is no longer true, and the next person
 * to add a label would trust it.
 */
export const FEATURE_VALUES = ['reach', 'connect', 'flow', 'sense'] as const;
export const featureSchema = z.enum(FEATURE_VALUES);
export type Feature = z.infer<typeof featureSchema>;

/**
 * The order pillars are **shown** in, everywhere: Reach → Connect → Sense →
 * Flow. The sidebar, the navigation tree, the Overview's pillar rows and the
 * admin feature grids all iterate this, not {@link FEATURE_VALUES}.
 *
 * It is a separate constant because the two orders answer different questions.
 * The database's order is an accident of when each label was added — Postgres
 * cannot move a label once it exists — while this one is the story the product
 * tells: get people (Reach), talk to the ones you hold (Connect), learn what
 * worked (Sense), and act on it per person (Flow). (Until slice 69 the last
 * place was "the pillar that is not built yet"; Flow is built now.)
 *
 * `satisfies` keeps it inside the vocabulary and nothing more: it accepts a
 * list that leaves a label out or names one twice. So the line under it is a
 * compile-time check that every `Feature` is in the list — a label added to
 * {@link FEATURE_VALUES} and forgotten here stops the build, rather than
 * reaching the admin feature grids, which rebuild a tenant's set from this
 * list and would silently drop the forgotten label from every tenant whose
 * box somebody ticked (replace-semantics on `PATCH /admin/tenants/:id`). The
 * navigation tree's refine adds the duplicate check, which a type cannot make.
 */
export const PILLAR_ORDER = ['reach', 'connect', 'sense', 'flow'] as const satisfies readonly Feature[];

/** Every `Feature` missing from {@link PILLAR_ORDER}; `never` while it is complete. */
type FeatureMissingFromPillarOrder = Exclude<Feature, (typeof PILLAR_ORDER)[number]>;
/** Fails to compile, naming the label, the moment a feature is left out of the display order. */
export const PILLAR_ORDER_IS_EXHAUSTIVE: [FeatureMissingFromPillarOrder] extends [never] ? true : FeatureMissingFromPillarOrder =
  true;

export const SUPPRESSION_REASON_VALUES = [
  'unsubscribe',
  'hard_bounce',
  'complaint',
  'manual',
] as const;
export const suppressionReasonSchema = z.enum(SUPPRESSION_REASON_VALUES);
export type SuppressionReason = z.infer<typeof suppressionReasonSchema>;

/**
 * Lifecycle of an uploaded dataset. Introduced by slice 1 alongside the
 * `datasets` table and mirrored as pg enum `dataset_status` in
 * `migrations/0003_contacts.sql`.
 *
 *   pending    — file stored, columns detected, waiting for a confirmed mapping
 *   processing — the import job is running
 *   ready      — the import finished; `imported_count` / `rejected_count` are final
 *   failed     — the import gave up; `datasets.error` says why (a CHECK enforces it)
 *
 * The import is asynchronous from day one — in-process now, on a queue later —
 * so every consumer must treat `pending`/`processing` as a normal steady state
 * and poll rather than assume a dataset is importable the moment it is created.
 */
export const DATASET_STATUS_VALUES = ['pending', 'processing', 'ready', 'failed'] as const;
export const datasetStatusSchema = z.enum(DATASET_STATUS_VALUES);
export type DatasetStatus = z.infer<typeof datasetStatusSchema>;

/**
 * What kind of segment this is. Introduced by slice 2 alongside the `segments`
 * table and mirrored as pg enum `segment_type` in
 * `migrations/0005_segments.sql`; the second label arrives in
 * `migrations/0036_segment_type_cohort.sql`.
 *
 *   static — a named union of one or more DATASETS, chosen by a person
 *   cohort — a SNAPSHOT of the people one Overview count counted, at one instant
 *   rule   — a DYNAMIC audience: a rule tree over the field catalogue, whose
 *            members (`segment_people`, by person) are refreshed when the data
 *            changes. Slice 67, `migrations/0049_segment_type_rule.sql`.
 *
 * The first two differ in what membership *is*, which is why they are two labels
 * rather than one kind carrying a flag. A `static` segment holds
 * `segment_datasets` rows and therefore owns no people: re-import an upload and
 * every segment including it changes, which is what makes its counts honest. A
 * `cohort` holds `segment_contacts` rows — every `contacts` row of every person
 * one card counted — because a set of PEOPLE cannot be expressed as a set of
 * datasets at all, and slice 50 exists so that *"1,284 people have not heard
 * from you in 90 days"* can become an audience somebody sends to.
 *
 * The slice 2 comment here said `static` was "the only member in v1" and that a
 * later **rule-based** kind was one `ALTER TYPE`. The first clause is now false
 * and the second was misleading. Rule-based segments are slice 55 and arrive as
 * a THIRD label beside these two, never as a replacement for `cohort`: every
 * population a cohort can hold is defined by the absence of a message, so
 * sending to it destroys it, and a rule re-evaluated at materialisation time
 * would find nobody.
 *
 * Slice 67 is that third label (the work was renumbered from 55). A rule
 * segment's membership is a third table, `segment_people`, with the same
 * `segment_type` composite foreign key, so the three membership limbs stay
 * provably disjoint.
 *
 * ORDER MATTERS. A Postgres enum remembers the order its labels were created
 * in, `ALTER TYPE ... ADD VALUE` appends, and `packages/db/src/schema/segments.ts`
 * hands this exact tuple to `pgEnum`. `static` stays first: every existing row
 * and the column DEFAULT depend on it.
 */
export const SEGMENT_TYPE_VALUES = ['static', 'cohort', 'rule'] as const;
export const segmentTypeSchema = z.enum(SEGMENT_TYPE_VALUES);
export type SegmentType = z.infer<typeof segmentTypeSchema>;

/**
 * What a file hanging off a template is. Introduced by slice 4 alongside the
 * `template_assets` table and mirrored as pg enum `template_asset_kind` in
 * `migrations/0007_templates.sql`.
 *
 *   inline_image — an image **referenced by an `image` block**, in the body
 *   attachment   — a file **referenced by nothing**, going out with every
 *                  message sent from the template
 *
 * Since slice 23 neither necessarily travels *inside* the message: where an
 * asset host is configured a picture is fetched from it and a file is linked
 * from a section under the footer. What has not changed is the sentence below,
 * which is about what references what.
 *
 * That difference is not a detail, and it is the reason these are one enum
 * rather than two tables: they share a row shape and a storage path, and differ
 * in exactly one behaviour that is easy to get catastrophically wrong. **Any
 * cleanup that says "delete assets no block mentions" is correct for
 * `inline_image` and destroys every `attachment`.** The sweep in
 * `templates.service.ts` filters on the kind for precisely this reason;
 * `0009_attachment_mime_types.sql` says the same thing from the database side.
 *
 * `attachment` was added in `0008_attachment_kind.sql`. It was deliberately
 * absent in slice 4's first cut, because SES v2's simple content cannot carry an
 * attachment and an upload endpoint accepting a PDF nobody could send would have
 * been a lie told by a working button. **Both halves exist now**: slice 5's
 * raw-MIME path builds every message as raw MIME and has carried attachments
 * since, and slice 17 took the "cannot be sent yet" sentence off the editor,
 * where it had outlived its premise by several slices. This paragraph said the
 * send path "does not exist yet" until slice 23 read it again.
 *
 * ## Slice 9 — `wa_header_media`, and why it is not `attachment`
 *
 * SUPERSEDED, and stated so rather than deleted: until slice 9 this vocabulary
 * described **email's** files only, and the two members above were the whole
 * list. Slice 9 puts the bytes behind a WhatsApp media header in the same table,
 * which is decision 1 of the slice — reuse `template_assets`, do not add a
 * second table for a row shape and a storage path that are already identical.
 *
 *   wa_header_media — the image, PDF or video behind a WhatsApp template's media
 *                     header. **Referenced by no block**, exactly like an
 *                     `attachment`, and for a different reason: a WhatsApp
 *                     document has no blocks at all.
 *
 * **It is a third member and not a reuse of `attachment`,** and that is the
 * whole point. Slice 4's review caught that a sweep phrased as "delete assets no
 * block mentions" deletes every attachment; a WA header asset has the identical
 * property, so riding on `attachment` would have made the two
 * indistinguishable to every filter that already exists — the orphan sweep, the
 * per-template attachment budget, the MIME allow-list, and the multer ceiling,
 * none of which mean the same thing for a 16 MB `.mp4` bound for Meta as they do
 * for a brochure bound for SES.
 *
 * To stop that mistake being *re-*makeable, the sweep's input is derived here
 * rather than restated at the call site — see
 * {@link BLOCK_REFERENCED_TEMPLATE_ASSET_KIND_VALUES}.
 *
 * The two halves are declared separately and the full list is their
 * concatenation, so `TEMPLATE_ASSET_KIND_VALUES` cannot drift from the sum of
 * its parts and the order still matches the order Postgres learned the labels
 * in — `0007` created `inline_image`, `0008` added `attachment`, and slice 9's
 * migration adds `wa_header_media` last. A pg enum remembers insertion order;
 * re-ordering this tuple would make the mirror a lie.
 */
export const EMAIL_TEMPLATE_ASSET_KIND_VALUES = ['inline_image', 'attachment'] as const;

/**
 * The kinds that hang off a **WhatsApp** template. One member today.
 *
 * Separate from the email half because the two are validated by different rules
 * and uploaded through different routes: an email asset is checked against
 * `TEMPLATE_IMAGE_MIME_TYPES`/`TEMPLATE_ATTACHMENT_MIME_TYPES` and slice 4's
 * byte caps, a WA header asset against Meta's per-format ceilings in
 * `./contracts/whatsapp-media.js`. Keeping the halves apart is what lets
 * `uploadTemplateAssetQuerySchema` accept only the email kinds, so widening this
 * enum did not silently open slice 4's inline-image route to a WhatsApp video.
 */
export const WA_TEMPLATE_ASSET_KIND_VALUES = ['wa_header_media'] as const;

export const TEMPLATE_ASSET_KIND_VALUES = [
  ...EMAIL_TEMPLATE_ASSET_KIND_VALUES,
  ...WA_TEMPLATE_ASSET_KIND_VALUES,
] as const;
export const templateAssetKindSchema = z.enum(TEMPLATE_ASSET_KIND_VALUES);
export type TemplateAssetKind = z.infer<typeof templateAssetKindSchema>;

/**
 * The kinds a **block in the document can point at**, and therefore the only
 * kinds the save-time orphan sweep may consider deleting.
 *
 * This constant exists so that the single most dangerous line in
 * `templates.service.ts` — the one that decides which unreferenced rows to drop
 * — reads its allow-list from the vocabulary rather than carrying a literal.
 * The literal version was correct when there were two kinds and would still
 * *look* correct with four; this version is wrong only if somebody adds a kind
 * to this list, which is a line of code that says what it is doing.
 *
 * **The safe default is exclusion.** A new kind is not swept until it is named
 * here, so the failure mode of forgetting is an object that costs storage, not
 * a file that is gone.
 */
export const BLOCK_REFERENCED_TEMPLATE_ASSET_KIND_VALUES = ['inline_image'] as const;

export function isBlockReferencedAssetKind(kind: TemplateAssetKind): boolean {
  return (BLOCK_REFERENCED_TEMPLATE_ASSET_KIND_VALUES as readonly string[]).includes(kind);
}

/**
 * The complement — every kind that is referenced by nothing and must therefore
 * survive a sweep. Derived, never typed out: an "and the others" list written by
 * hand is a list that goes stale the moment a kind is added, and the way it goes
 * stale is that the new kind is missing from it and gets deleted.
 */
export const UNREFERENCED_TEMPLATE_ASSET_KIND_VALUES: readonly TemplateAssetKind[] =
  TEMPLATE_ASSET_KIND_VALUES.filter((kind) => !isBlockReferencedAssetKind(kind));

/**
 * The kinds a **recipient fetches over the web**, and therefore the only kinds
 * that may hold a `public_id`.
 *
 * Mirrors `template_assets_public_id_is_hostable_kind` — 0021's CHECK as 0024
 * widened it — and exists for the same reason
 * {@link BLOCK_REFERENCED_TEMPLATE_ASSET_KIND_VALUES} does: the upload path
 * decides whether to mint a public address from this list rather than from a
 * literal, so one place decides it for every caller.
 *
 * **It is a hand-maintained mirror, not a derivation**, exactly like the schema
 * mirror in `@wend/db`: adding a kind here and not to a migration still produces
 * a 23514 on a real upload. What it buys is that the decision is made once and
 * read by name, so the drift is between this line and one SQL file rather than
 * between the CHECK and however many call sites had typed the literal.
 *
 *   inline_image — fetched by the reader's mail client, since slice 21
 *   attachment   — fetched by the reader, from a link under the footer, since
 *                  slice 23
 *
 * `wa_header_media` is absent and must stay absent: that file is handed to Meta
 * through Resumable Upload and is never served from Wend's host at all.
 *
 * **The safe default is exclusion.** A kind not named here is never published,
 * so the cost of forgetting is a file that keeps travelling inside the message —
 * not bytes on the open web that nobody decided to put there.
 */
export const HOSTABLE_TEMPLATE_ASSET_KIND_VALUES = ['inline_image', 'attachment'] as const;

export function isHostableAssetKind(kind: TemplateAssetKind): boolean {
  return (HOSTABLE_TEMPLATE_ASSET_KIND_VALUES as readonly string[]).includes(kind);
}

/**
 * What a report is *about*. Introduced by slice 13 alongside the `report_runs`
 * table, and the discriminator on {@link reportDefinitionSchema}.
 *
 *   credit_usage — the credit movements of a window, one worksheet per channel
 *
 * A single-member enum for the same reason `segment_type` is one: the column is
 * the extension point, and it is here rather than in `./contracts/report.ts`
 * because *this* file is what `@wend/db` mirrors as a Postgres type. A second
 * report kind is then one `ALTER TYPE … ADD VALUE` and one member, not a table.
 *
 * The rejected alternative was a `text` column with no vocabulary at all, on the
 * `delivery_events.event_type` precedent. That precedent does not apply: an
 * event type is a *provider's* word arriving from outside and must not need a
 * migration, whereas a report kind is ours and nothing outside Wend can invent
 * one. A free-text kind here just means a typo becomes a report nobody can run.
 */
/* `surface_results` — slice 44, migration 0033: one worksheet per surface. */
export const REPORT_KIND_VALUES = ['credit_usage', 'surface_results'] as const;
export const reportKindSchema = z.enum(REPORT_KIND_VALUES);
export type ReportKind = z.infer<typeof reportKindSchema>;

/**
 * What comes out. `xlsx` only, and the format is on the definition rather than
 * assumed by the generator — a CSV export is a v2 conversation that must not
 * require rewriting the run record to have one.
 */
export const REPORT_FORMAT_VALUES = ['xlsx'] as const;
export const reportFormatSchema = z.enum(REPORT_FORMAT_VALUES);
export type ReportFormat = z.infer<typeof reportFormatSchema>;

/**
 * Where a run has got to.
 *
 *   queued    — accepted, nothing has been read yet
 *   running   — the generator started
 *   succeeded — a file exists, in tenant-scoped storage
 *   failed    — it stopped, and the run says why
 *
 * **`failed` is a member, not the absence of `succeeded`.** SLICES slice 13:
 * *a failed run is visible as a failed run, not as a missing file*. Four states
 * rather than a `finished_at`-plus-nullable-`error` pair, because that pair can
 * express "finished, no file, no error", which is the exact state a screen
 * renders as "no report" when the truth is "the report failed" — the same
 * zero-versus-absent rule this codebase applies to every number, applied to a
 * job. `./contracts/report.ts` makes the four states a discriminated union so
 * the impossible combinations cannot be constructed at all.
 */
export const REPORT_RUN_STATUS_VALUES = ['queued', 'running', 'succeeded', 'failed'] as const;
export const reportRunStatusSchema = z.enum(REPORT_RUN_STATUS_VALUES);
export type ReportRunStatus = z.infer<typeof reportRunStatusSchema>;

/**
 * What started a run: a person, or a schedule.
 *
 * The binding constraint of slice 13 is *"I need to automate this in future, so
 * keep it that way"*, and this two-member vocabulary is where that constraint
 * becomes structure: the generator takes a run, and a run always knows which of
 * these two started it. There is no third member for "unknown" on purpose — a
 * run with no attribution is a run nobody can explain the existence of, and the
 * moment such a member exists it becomes the default anything unattributed is
 * written with.
 */
export const REPORT_TRIGGER_KIND_VALUES = ['user', 'schedule'] as const;
export const reportTriggerKindSchema = z.enum(REPORT_TRIGGER_KIND_VALUES);
export type ReportTriggerKind = z.infer<typeof reportTriggerKindSchema>;
